import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { Descendant } from 'slate';

import { ReviewScheduleType } from '@/constants/review/ReviewScheduleType';

interface TextNode {
    text: string;
}

interface ElementNode {
    children: (TextNode | ElementNode)[];
}

const getCharacterCount = (value: Descendant[]): number => {
    return value
        .map((node) => {
            const element = node as ElementNode;
            return element.children
                .map((child) => {
                    if ('text' in child) {
                        return (child as TextNode).text;
                    }
                    return '';
                })
                .join('');
        })
        .join('').length;
};

const DAYS_MIN_MESSAGE: TranslationKey = 'evaluation360ScheduleCreation.validation.daysMin';
const DAYS_MAX_MESSAGE: TranslationKey = 'evaluation360ScheduleCreation.validation.daysMax';
const SCHEDULED_AT_REQUIRED_MESSAGE: TranslationKey =
    'cycleEvaluationSchedule.validation.scheduledAtRequired';
const FEEDBACK_FREQUENCY_REQUIRED_MESSAGE: TranslationKey =
    'cycleEvaluationSchedule.validation.feedbackFrequencyRequired';
const EMAIL_TITLE_MIN_MESSAGE: TranslationKey =
    'evaluation360ScheduleCreation.validation.emailTitleMin';
const EMAIL_TITLE_MAX_MESSAGE: TranslationKey =
    'evaluation360ScheduleCreation.validation.emailTitleMax';
const EMAIL_MESSAGE_REQUIRED_MESSAGE: TranslationKey =
    'evaluation360ScheduleCreation.validation.emailMessageRequired';
const EMAIL_MESSAGE_MAX_MESSAGE: TranslationKey =
    'evaluation360ScheduleCreation.validation.emailMessageMax';

const EMAIL_MESSAGE_MAX_LENGTH = 3000;

const emailTitleSchema = z
    .string()
    .min(5, { message: EMAIL_TITLE_MIN_MESSAGE })
    .max(100, { message: EMAIL_TITLE_MAX_MESSAGE });

const emailMessageSchema = z
    .custom<Descendant[]>(
        (val) => {
            if (!Array.isArray(val)) {
                return false;
            }
            return getCharacterCount(val) > 0;
        },
        {
            message: EMAIL_MESSAGE_REQUIRED_MESSAGE,
        },
    )
    .refine(
        (val) => {
            return getCharacterCount(val) <= EMAIL_MESSAGE_MAX_LENGTH;
        },
        {
            message: EMAIL_MESSAGE_MAX_MESSAGE,
        },
    );

const ScheduleCreationFormFields = {
    daysToComplete: 'daysToComplete',
    scheduleType: 'scheduleType',
    scheduledAt: 'scheduledAt',
    feedbackFrequency: 'feedbackFrequency',
    reminderSevenDays: 'reminderSevenDays',
    reminderThreeDays: 'reminderThreeDays',
    reminderOnDeadline: 'reminderOnDeadline',
    managerViewScores: 'managerViewScores',
    managerReadComments: 'managerReadComments',
    managerAccessIndividual: 'managerAccessIndividual',
    emailLanguage: 'emailLanguage',
    emailTitle: 'emailTitle',
    emailMessage: 'emailMessage',
} as const;

const scheduleCreationSchema = z.object({
    [ScheduleCreationFormFields.daysToComplete]: z
        .number()
        .min(1, { message: DAYS_MIN_MESSAGE })
        .max(365, { message: DAYS_MAX_MESSAGE })
        .optional(),

    [ScheduleCreationFormFields.scheduleType]: z
        .enum([ReviewScheduleType.SEND_IMMEDIATELY, ReviewScheduleType.SCHEDULE_FOR_LATER])
        .optional(),

    [ScheduleCreationFormFields.scheduledAt]: z.date().refine(
        (date) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date >= today;
        },
        { message: SCHEDULED_AT_REQUIRED_MESSAGE },
    ),

    [ScheduleCreationFormFields.feedbackFrequency]: z
        .string()
        .min(1, { message: FEEDBACK_FREQUENCY_REQUIRED_MESSAGE })
        .optional(),

    [ScheduleCreationFormFields.reminderSevenDays]: z.boolean(),
    [ScheduleCreationFormFields.reminderThreeDays]: z.boolean(),
    [ScheduleCreationFormFields.reminderOnDeadline]: z.boolean(),

    [ScheduleCreationFormFields.managerViewScores]: z.boolean(),
    [ScheduleCreationFormFields.managerReadComments]: z.boolean(),
    [ScheduleCreationFormFields.managerAccessIndividual]: z.boolean(),

    [ScheduleCreationFormFields.emailLanguage]: z.string(),

    [ScheduleCreationFormFields.emailTitle]: emailTitleSchema,

    [ScheduleCreationFormFields.emailMessage]: emailMessageSchema,
});

type ScheduleCreationFormData = z.infer<typeof scheduleCreationSchema>;

export { ScheduleCreationFormFields, scheduleCreationSchema, emailTitleSchema, emailMessageSchema };
export type { ScheduleCreationFormData };
